import React, { useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";

// Components
import JournalAppointmentCard from "~/components/cards/journal/JournalAppointmentCard";
import Pagination from "~/components/_uikit/Pagination/Pagination";
// Styles
import s from "~/views/appointment/journal/JournalPage.module.scss";
// Types
import RootStore from "~/store/RootStore";
// Hooks
import { useStore } from "~/hooks/useStore";
import { useJournalAppointements } from "~/hooks/useJournalAppointements";
import { useNavigate } from "react-router-dom";
import ROUTES from "~/utils/routes";
import { logEvent } from "~/utils/amplitude";
import { createTicketPdf } from "~/utils/jspdf";

interface Props {}

const JournalPage: React.FC<Props> = observer(() => {
  const store: RootStore = useStore();
  const navigate = useNavigate();
  const { passedAppointments, upcomingAppointments } =
    useJournalAppointements();

  useEffect(() => {
    logEvent("Э/ Страница с историей записи");
  }, []);

  useEffect(() => {
    if (store.patientStore.currentPatient?.idUserData) {
      store.journalStore.fetchJournal({
        id_user_data: store.patientStore.currentPatient.idUserData,
      });
    } else {
      navigate(ROUTES.LOGIN.PATHNAME, { replace: true });
    }
  }, [navigate, store.journalStore, store.patientStore]);

  const changePage = useCallback(
    (page: number) => {
      store.journalStore.pagination.setCurrentPage(page);
    },
    [store.journalStore.pagination]
  );

  useEffect(() => {
    store.journalStore.pagination.setItemsDisplayedOnPage(4);
  }, [store.journalStore.pagination]);

  const upcomingAppointmentsHandler = {
    onCancel(id: number) {
      const foundAppointment = store.journalStore.appointmentsItems.find(
        (item) => {
          return item.id === id;
        }
      );

      logEvent("Д/ Нажал отменить запись / История записей", {
        "Ид записи": id,
      });

      if (foundAppointment) {
        store.appointmentStore.cancelAppointment({
          id_doctor: foundAppointment.idDoctor,
          id_journal_ticket: foundAppointment.idJournalTicket,
          id_ticket_external: foundAppointment.idTicket,
        });
      }
    },

    onDownload(id: number) {
      const foundAppointment = store.journalStore.appointmentsItems.find(
        (item) => {
          return item.id === id;
        }
      );

      foundAppointment && createTicketPdf(foundAppointment);
    },
  };

  const passedAppointmentsHandler = {};

  return (
    <div className="screen-container">
      {upcomingAppointments.length > 0 && (
        <>
          <div className={s.layoutTitle}>
            <h2 className="headline-2">Ближайшие записи:</h2>
          </div>
          <div className={s.layoutUpcomingAppointments}>
            {upcomingAppointments.map((journalItem) => {
              return (
                <JournalAppointmentCard
                  actions={upcomingAppointmentsHandler}
                  journalAppointment={journalItem}
                  key={journalItem.id}
                />
              );
            })}
          </div>
        </>
      )}

      {passedAppointments.length > 0 && (
        <>
          <div className={s.layoutTitle}>
            <h2 className="headline-2">Прошедшие записи:</h2>
          </div>
          <div className={s.layoutPassedAppointments}>
            {passedAppointments.map((journalItem) => {
              return (
                <JournalAppointmentCard
                  actions={passedAppointmentsHandler}
                  journalAppointment={journalItem}
                  key={journalItem.id}
                />
              );
            })}
          </div>
        </>
      )}

      <div className={s.layout}>
        <div className={s.layoutPaginationContainer}>
          {!store.journalStore.pagination.noPagesOrSinglePage && (
            <Pagination
              count={store.journalStore.pagination.amountOfPages}
              currentPage={store.journalStore.pagination.currentPage}
              onChange={changePage}
              canGoPreviousPage={
                store.journalStore.pagination.canGoPreviousPage
              }
              canGoNextPage={store.journalStore.pagination.canGoNextPage}
              goPreviousPage={store.journalStore.pagination.goPreviousPage}
              goNextPage={store.journalStore.pagination.goNextPage}
            />
          )}
        </div>
      </div>

      {upcomingAppointments.length === 0 && passedAppointments.length === 0 && (
        <>
          <div className={s.emptyTextContainer}>
            <p className="headline-2">У вас еще не было посещений</p>
          </div>
        </>
      )}
    </div>
  );
});

export default JournalPage;
